import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import LoadingSpinner from '../LoadingSpinner';

const STATUS_STYLE = {
  pending: { text: '待確認', className: 'bg-yellow-100 text-yellow-700' },
  confirmed: { text: '已確認', className: 'bg-green-100 text-green-700' },
  completed: { text: '已完成', className: 'bg-blue-100 text-blue-700' },
  cancelled: { text: '已取消', className: 'bg-red-100 text-red-700' },
};

function getStatusBadge(status) {
  return STATUS_STYLE[status?.toLowerCase()] || { text: status || '未知', className: 'bg-gray-100 text-gray-600' };
}

function formatDate(dateString) {
  if (!dateString) return '未知日期';
  return new Date(dateString).toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime(timeString) {
  if (!timeString) return '';
  return timeString.slice(0, 5); // HH:MM
}

const BookingHistory = () => {
  const { user, isAuthenticated } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, pagination.page]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await authService.getUserBookings(user.user_id, {
        page: pagination.page,
        limit: pagination.limit,
      });

      setBookings(response.results || response.data || []);
      const total = response.count || response.total || 0;
      setPagination(prev => ({ ...prev, total, totalPages: Math.ceil(total / prev.limit) }));
      setError(null);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setError('無法載入體驗記錄，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center w-full">
          <h2 className="text-lg font-bold text-gray-800 mb-2">請先登入</h2>
          <p className="text-sm text-gray-500 mb-6">登入先睇到你嘅體驗記錄</p>
          <a
            href="/login"
            className="inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-blue-600"
          >
            前往登入
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6 bg-gray-50 min-h-screen pb-24">
      <h1 className="text-2xl font-black text-gray-800 mb-8">體驗記錄</h1>

      {loading ? (
        <LoadingSpinner size="lg" />
      ) : error ? (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={fetchBookings}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-blue-600"
          >
            重新載入
          </button>
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-800 font-bold mb-1">尚未有體驗記錄</p>
          <p className="text-sm text-gray-500">立即去玩車啦！</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => {
            const badge = getStatusBadge(booking.status);
            return (
              <div
                key={booking.id}
                className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-base font-bold text-gray-800">
                    {booking.service_name || '玩車體驗'}
                  </p>
                  <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-bold ${badge.className}`}>
                    {badge.text}
                  </span>
                </div>

                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-3">商戶</p>
                <p className="text-sm text-gray-700 mt-0.5">{booking.merchant_name || 'KaKa卡卡兒童超跑'}</p>

                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-3">時間</p>
                <p className="text-sm text-gray-700 mt-0.5">
                  {formatDate(booking.booking_date)}{booking.booking_time ? ` ${formatTime(booking.booking_time)}` : ''}
                </p>

                {booking.notes && (
                  <>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-3">備註</p>
                    <p className="text-sm text-gray-700 mt-0.5">{booking.notes}</p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="px-4 py-2 rounded-xl text-sm font-bold text-gray-600 bg-white border border-gray-200 disabled:opacity-40"
          >
            上一頁
          </button>
          <span className="text-sm text-gray-500">
            第 {pagination.page} / {pagination.totalPages} 頁
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="px-4 py-2 rounded-xl text-sm font-bold text-gray-600 bg-white border border-gray-200 disabled:opacity-40"
          >
            下一頁
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;
